import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Form,
  Row,
  Col,
  Card,
  Modal,
  Spinner,
  Badge,
} from "react-bootstrap";
import axios from "axios";
import API_BASE_URL from "../config";

const PayrollView = ({ employeeId }) => {
  const [structure, setStructure] = useState({
    earnings: [],
    deductions: [],
    rates: [],
    country: "Unknown",
    currency: "$",
  });
  const [payrollRecords, setPayrollRecords] = useState([]);
  const [showStructureModal, setShowStructureModal] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [loadingPayslip, setLoadingPayslip] = useState(null); // null or record.id
  const [structureForm, setStructureForm] = useState({
    components: [],
    country_id: "",
  });
  const [generateForm, setGenerateForm] = useState({
    payroll_month: "",
    overtime_hours: 0,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchStructure();
    fetchPayrollRecords();
  }, [employeeId]);

  const fetchStructure = async () => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/employees/${employeeId}/salary_structure`
      );
      setStructure({
        ...res.data,
        currency: res.data.currency || "$",
      });

      const allComps = [
        ...res.data.earnings.map((c) => ({
          component_id: c.id,
          amount: c.amount,
          name: c.name,
          type: "earning",
        })),
        ...res.data.deductions.map((c) => ({
          component_id: c.id,
          amount: c.amount,
          name: c.name,
          type: "deduction",
        })),
        ...res.data.rates.map((c) => ({
          component_id: c.id,
          amount: c.amount,
          name: c.name,
          type: "rate",
        })),
      ];

      setStructureForm({
        components: allComps,
        country_id: res.data.country_id || "",
      });
    } catch (err) {
      console.error(err);
    }
  };

  const fetchPayrollRecords = async () => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/employees/${employeeId}/payroll_records`
      );
      setPayrollRecords(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleStructureChange = (compId, value) => {
    setStructureForm((prev) => ({
      ...prev,
      components: prev.components.map((comp) =>
        comp.component_id === compId ? { ...comp, amount: value } : comp
      ),
    }));
  };

  const handleStructureSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.put(
        `${API_BASE_URL}/employees/${employeeId}/salary_structure`,
        {
          components: structureForm.components.map((c) => ({
            component_id: c.component_id,
            amount: parseFloat(c.amount) || 0,
          })),
          country_id: structureForm.country_id,
          effective_date: new Date().toISOString().split("T")[0],
        }
      );
      setShowStructureModal(false);
      fetchStructure();
    } catch (err) {
      console.error(err);
      alert("Failed to save. Check console.");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const monthStr = generateForm.payroll_month.replace("-", "");
      const res = await axios.post(
        `${API_BASE_URL}/employees/${employeeId}/generate_payroll`,
        {
          payroll_month: parseInt(monthStr, 10),
          overtime_hours: parseFloat(generateForm.overtime_hours) || 0,
        }
      );

      if (res.data.currency) {
        setStructure((prev) => ({ ...prev, currency: res.data.currency }));
      }

      setShowGenerateModal(false);
      fetchPayrollRecords();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePayslip = async (recordId, format) => {
    setLoadingPayslip(recordId); // Show loader
    setLoading(true);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/payroll_records/${recordId}/payslip`,
        { format }
      );
      const link = document.createElement("a");
      link.href = `${API_BASE_URL}/${res.data.download_url}`;
      link.download = "";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
      setLoadingPayslip(null); // Hide loader
    }
  };

  const openStructureModal = () => {
    fetchStructure().then(() => setShowStructureModal(true));
  };

  const { currency = "$" } = structure;

  return (
    <div>
      <h4>Payroll & Compensation</h4>

      {/* Salary Structure */}
      <Card className="mb-4">
        <Card.Body>
          <Row className="align-items-center">
            <Col md={10}>
              <h5>Salary Structure ({structure.country})</h5>
            </Col>
            <Col md={2}>
              <Button onClick={openStructureModal} size="sm">
                Edit
              </Button>
            </Col>
          </Row>
          <Table>
            <thead>
              <tr>
                <th>Component</th>
                <th>Type</th>
                <th>Amount/Rate</th>
              </tr>
            </thead>
            <tbody>
              {structure.earnings.map((comp) => (
                <tr key={comp.id}>
                  <td>{comp.name}</td>
                  <td>Earning</td>
                  <td>
                    {currency}
                    {comp.amount}
                  </td>
                </tr>
              ))}
              {structure.deductions.map((comp) => (
                <tr key={comp.id}>
                  <td>{comp.name}</td>
                  <td>Deduction</td>
                  <td>
                    {currency}
                    {comp.amount}
                  </td>
                </tr>
              ))}
              {structure.rates.map((comp) => (
                <tr key={comp.id}>
                  <td>{comp.name}</td>
                  <td>Rate</td>
                  <td>{comp.amount}x</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Generate Payroll */}
      <Card className="mb-4">
        <Card.Body>
          <h5>Generate Payroll</h5>
          <Button onClick={() => setShowGenerateModal(true)}>Generate</Button>
        </Card.Body>
      </Card>

      {/* Payroll Records */}
      <h5>Payroll Records</h5>
      <Table>
        <thead>
          <tr>
            <th>Month</th>
            <th>Type</th>
            <th>Gross</th>
            <th>Net</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {payrollRecords.map((record) => {
            const breakdown = record.payroll_breakdown
              ? JSON.parse(record.payroll_breakdown)
              : {};
            const payrollType =
              breakdown.type === "commission" ? "Commission" : "Fixed";
            return (
              <tr key={record.id}>
                <td>{record.payroll_month}</td>
                <td>
                  <Badge
                    bg={payrollType === "Commission" ? "success" : "primary"}
                  >
                    {payrollType}
                  </Badge>
                </td>
                <td>
                  {currency}
                  {record.gross_salary}
                </td>
                <td>
                  {currency}
                  {record.net_salary}
                </td>
                <td>{record.status}</td>
                <td>
                  {loadingPayslip === record.id ? (
                    <Spinner size="sm" animation="border" /> // or your custom loader
                  ) : (
                    <>
                      <Button
                        onClick={() => handlePayslip(record.id, "pdf")}
                        size="sm"
                        disabled={loadingPayslip !== null}
                      >
                        PDF
                      </Button>{" "}
                      <Button
                        onClick={() => handlePayslip(record.id, "excel")}
                        size="sm"
                        disabled={loadingPayslip !== null}
                      >
                        Excel
                      </Button>
                    </>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>

      {/* Edit Structure Modal */}
      <Modal
        show={showStructureModal}
        onHide={() => setShowStructureModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Salary Structure</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loading && <Spinner animation="border" />}
          <Form onSubmit={handleStructureSubmit}>
            <h6>Earnings</h6>
            {structureForm.components
              .filter((c) => c.type === "earning")
              .map((comp) => {
                const formComp = structureForm.components.find(
                  (f) => f.component_id === comp.component_id
                );
                return (
                  <Form.Group key={comp.component_id} className="mb-2">
                    <Form.Label>{comp.name}</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      value={formComp?.amount || 0}
                      onChange={(e) =>
                        handleStructureChange(comp.component_id, e.target.value)
                      }
                      placeholder="0.00"
                    />
                  </Form.Group>
                );
              })}

            <h6 className="mt-4">Deductions</h6>
            {structureForm.components
              .filter((c) => c.type === "deduction")
              .map((comp) => {
                const formComp = structureForm.components.find(
                  (f) => f.component_id === comp.component_id
                );
                return (
                  <Form.Group key={comp.component_id} className="mb-2">
                    <Form.Label>{comp.name}</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      value={formComp?.amount || 0}
                      onChange={(e) =>
                        handleStructureChange(comp.component_id, e.target.value)
                      }
                      placeholder="0.00"
                    />
                  </Form.Group>
                );
              })}

            <h6 className="mt-4">Rates</h6>
            {structureForm.components
              .filter((c) => c.type === "rate")
              .map((comp) => {
                const formComp = structureForm.components.find(
                  (f) => f.component_id === comp.component_id
                );
                return (
                  <Form.Group key={comp.component_id} className="mb-2">
                    <Form.Label>{comp.name}</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      value={formComp?.amount || 0}
                      onChange={(e) =>
                        handleStructureChange(comp.component_id, e.target.value)
                      }
                      placeholder="1.5"
                    />
                  </Form.Group>
                );
              })}

            <Button type="submit" className="mt-3" disabled={loading}>
              {loading ? "Saving..." : "Save Changes"}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Generate Payroll Modal */}
      <Modal
        show={showGenerateModal}
        onHide={() => setShowGenerateModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Generate Payroll</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleGenerateSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Month</Form.Label>
              <Form.Control
                type="month"
                value={generateForm.payroll_month}
                onChange={(e) =>
                  setGenerateForm({
                    ...generateForm,
                    payroll_month: e.target.value,
                  })
                }
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Overtime Hours</Form.Label>
              <Form.Control
                type="number"
                step="0.5"
                value={generateForm.overtime_hours}
                onChange={(e) =>
                  setGenerateForm({
                    ...generateForm,
                    overtime_hours: e.target.value,
                  })
                }
              />
            </Form.Group>
            <Button type="submit">Generate Payroll</Button>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default PayrollView;
